import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Globe } from 'lucide-react';

const Header = () => {
  const { isArabic, toggleLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Function to handle the "Back to Top" logic
  const handleLogoClick = (e) => {
    // If we're already on the home page, prevent default link behavior and scroll smoothly
    if (location.pathname === "/") {
      e.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
    // If mobile menu is open, close it
    setIsMenuOpen(false);
  };

  const isInternalPage = location.pathname !== "/";

  return (
    <nav className={`fixed w-full z-[100] transition-all duration-500 ${
      isScrolled || isInternalPage 
        ? 'bg-white/90 backdrop-blur-md py-4 shadow-lg' 
        : 'bg-transparent py-6'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex justify-between items-center ${isArabic ? 'flex-row-reverse' : ''}`}>
          
          {/* --- LOGO SECTION (Dances on Hover, Scrolls to top on Click) --- */}
          <Link 
            to="/" 
            onClick={handleLogoClick}
            className="flex items-center gap-2 group cursor-pointer"
          >
            {/* Add dir="ltr" here to prevent the flip */}
            <div dir="ltr" className="flex text-2xl font-black tracking-tighter overflow-hidden">
              {['A', 'F', 'A', 'M'].map((letter, index) => (
                <span 
                  key={index}
                  className="text-red-600 inline-block transition-transform duration-300 group-hover:animate-bounce"
                  style={{ 
                    animationDelay: `${index * 0.1}s`, 
                    animationIterationCount: 1 
                  }}
                >
                  {letter}
                </span>
              ))}
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className={`hidden md:flex items-center gap-8 ${isArabic ? 'flex-row-reverse' : ''}`}>
            {['About', 'Branches', 'Team', 'Contact'].map((item) => (
              <a 
                key={item}
                href={`/#${item.toLowerCase()}`}
                className={`text-sm font-bold uppercase tracking-widest transition-all hover:text-saudi-green ${
                  isScrolled || isInternalPage ? 'text-corp-blue' : 'text-white'
                }`}
              >
                {isArabic ? (item === 'About' ? 'من نحن' : item === 'Branches' ? 'فروعنا' : item === 'Team' ? 'الفريق' : 'اتصل بنا') : item}
              </a>
            ))}

            <button 
              onClick={toggleLanguage}
              className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all ${
                isScrolled || isInternalPage 
                  ? 'border-corp-blue text-corp-blue hover:bg-corp-blue hover:text-white' 
                  : 'border-white/30 text-white hover:bg-white hover:text-corp-blue'
              }`}
            >
              <Globe size={16} />
              <span className="text-xs font-bold">{isArabic ? 'English' : 'العربية'}</span>
            </button>
          </div>

          {/* Mobile Toggle */}
          <button 
            className="md:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? (
              <X className={isScrolled || isInternalPage ? 'text-corp-blue' : 'text-white'} />
            ) : (
              <Menu className={isScrolled || isInternalPage ? 'text-corp-blue' : 'text-white'} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div className={`absolute top-full left-0 w-full bg-white shadow-2xl transition-all duration-300 overflow-hidden ${
        isMenuOpen ? 'max-h-[400px] border-t' : 'max-h-0'
      }`}>
        <div className="flex flex-col p-6 gap-4 text-center">
          <a href="/#about" onClick={() => setIsMenuOpen(false)} className="font-bold text-corp-blue">About</a>
          <a href="/#branches" onClick={() => setIsMenuOpen(false)} className="font-bold text-corp-blue">Branches</a>
          <a href="/#team" onClick={() => setIsMenuOpen(false)} className="font-bold text-corp-blue">Team</a>
          <Link to="/careers" onClick={() => setIsMenuOpen(false)} className="font-bold text-corp-blue">Careers</Link>
          <button onClick={toggleLanguage} className="bg-saudi-green text-white py-3 rounded-xl font-bold">
            {isArabic ? 'Switch to English' : 'تحويل للعربية'}
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Header;